import React from "react";
import {
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import EditIcon from "@mui/icons-material/Edit";
import BlockIcon from "@mui/icons-material/Block";
import AddIcon from "@mui/icons-material/Add";
import GroupsIcon from "@mui/icons-material/Groups";
import BusinessIcon from "@mui/icons-material/Business";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SearchIcon from "@mui/icons-material/Search";
import API from "../API";
import BusinessFormDialog from "../components/businesses/BusinessFormDialog";
import BusinessUserFormDialog from "../components/businesses/BusinessUserFormDialog";
import PasswordResultDialog from "../components/businesses/PasswordResultDialog";

const getErrorMessage = (response) => {
  if (response?.error === 3401 || response?.code === 3401) {
    return "Bu e-posta adresi zaten kullanılıyor.";
  }

  if (typeof response?.error === "string") return response.error;
  if (typeof response?.message === "string") return response.message;
  return "Bir şeyler ters gitti.";
};

const getNumericCount = (value) => {
  if (value === null || value === undefined || value === "") return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

export default class BusinessDetailScreen extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      loading: true,
      usersLoading: true,
      businessSaving: false,
      userSaving: false,
      business: undefined,
      users: [],
      includeInactiveUsers: false,
      userSearch: "",
      businessDialogOpen: false,
      userDialogOpen: false,
      selectedUser: undefined,
      passwordDialogOpen: false,
      generatedPassword: "",
    };
  }

  componentDidMount() {
    this.loadBusiness();
    this.loadUsers();
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.match.params.id !== this.props.match.params.id) {
      this.loadBusiness();
      this.loadUsers();
    }

    if (
      prevState.includeInactiveUsers !== this.state.includeInactiveUsers &&
      prevProps.match.params.id === this.props.match.params.id
    ) {
      this.loadUsers();
    }
  }

  loadBusiness = async () => {
    this.setState({ loading: true });
    const response = await API.getBusiness({ _id: this.props.match.params.id });

    if (!response?._id) {
      global.showPopup("Hata", getErrorMessage(response));
      this.props.history.replace("/businesses");
      return;
    }

    this.setState({ business: response, loading: false });
  };

  loadUsers = async () => {
    this.setState({ usersLoading: true });
    const response = await API.getBusinessUsers({
      businessId: this.props.match.params.id,
      includeInactive: this.state.includeInactiveUsers,
    });

    this.setState({
      users: Array.isArray(response?.users) ? response.users : [],
      usersLoading: false,
    });
  };

  refreshAll = async () => {
    await Promise.all([this.loadBusiness(), this.loadUsers()]);
  };

  handleSaveBusiness = async (business) => {
    this.setState({ businessSaving: true });
    const response = await API.saveBusiness({ business });

    if (!response?.ok || !response?.business) {
      this.setState({ businessSaving: false });
      global.showPopup("Hata", getErrorMessage(response));
      return;
    }

    await this.loadBusiness();
    this.setState({ businessDialogOpen: false, businessSaving: false });
  };

  handleDeleteBusiness = () => {
    const business = this.state.business;
    if (!business?._id) return;

    global.openPrompt(
      "İşletmeyi sil",
      "Bu işlem işletmeyi siler ve bağlı kullanıcıları da devre dışı bırakır.",
      "Sil",
      async () => {
        this.setState({ businessSaving: true });
        const response = await API.deleteBusiness({ _id: business._id });

        if (!response?.ok) {
          this.setState({ businessSaving: false });
          global.showPopup("Hata", getErrorMessage(response));
          return;
        }

        this.props.history.replace("/businesses");
      }
    );
  };

  handleSaveUser = async (user) => {
    this.setState({ userSaving: true });
    const response = await API.saveBusinessUser({ user, passwordOnly: false });

    if (!response?.ok || !response?.user) {
      this.setState({ userSaving: false });
      global.showPopup("Hata", getErrorMessage(response));
      return;
    }

    await this.refreshAll();
    this.setState({
      userDialogOpen: false,
      selectedUser: undefined,
      userSaving: false,
      passwordDialogOpen: Boolean(response.password),
      generatedPassword: response.password || "",
    });
  };

  handleResetPassword = (user) => {
    global.openPrompt(
      "Şifreyi sıfırla",
      "Yeni şifre üretilecek ve mevcut şifre geçersiz olacak.",
      "Onayla",
      async () => {
        this.setState({ userSaving: true });
        const response = await API.saveBusinessUser({
          user: {
            _id: user._id,
            businessId: this.props.match.params.id,
            createPassword: true,
          },
          passwordOnly: true,
        });

        if (!response?.ok || !response?.password) {
          this.setState({ userSaving: false });
          global.showPopup("Hata", getErrorMessage(response));
          return;
        }

        this.setState({
          userSaving: false,
          passwordDialogOpen: true,
          generatedPassword: response.password,
        });
      }
    );
  };

  handleDeleteUser = (user) => {
    global.openPrompt(
      "Kullanıcıyı sil",
      "Bu işlem kullanıcıyı siler ve devre dışı bırakır.",
      "Sil",
      async () => {
        this.setState({ userSaving: true });
        const response = await API.deleteBusinessUser({ _id: user._id });

        if (!response?.ok) {
          this.setState({ userSaving: false });
          global.showPopup("Hata", getErrorMessage(response));
          return;
        }

        await this.refreshAll();
        this.setState({ userSaving: false });
      }
    );
  };

  render() {
    if (this.state.loading || !this.state.business) return <></>;

    const { business } = this.state;
    const businessUsers = Array.isArray(business.users) ? business.users : [];
    const totalUserCount =
      getNumericCount(business.userCount) ??
      (businessUsers.length > 0 ? businessUsers.length : this.state.users.length);
    const activeUserCount =
      getNumericCount(business.activeUserCount) ??
      (businessUsers.length > 0
        ? businessUsers.filter((user) => !user.deactive).length
        : this.state.users.filter((user) => !user.deactive).length);
    const filteredUsers = this.state.users.filter((user) => {
      const q = this.state.userSearch.trim().toLowerCase();
      if (!q) return true;

      return [user.name, user.email, user.phone]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q));
    });

    return (
      <div style={{ display: "flex", justifyContent: "center" }}>
        <div style={{ width: "100%", maxWidth: 1280 }}>
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <Badge style={{ marginTop: 8, padding: 0 }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    padding: "24px 28px 18px 28px",
                    borderBottom: "1px solid #E7EDF6",
                    gap: 16,
                    flexWrap: "wrap",
                  }}
                >
                  <div>
                    <Typography
                      style={{
                        color: "#64748B",
                        fontSize: 12,
                        fontWeight: 700,
                        letterSpacing: "0.14em",
                        textTransform: "uppercase",
                        marginBottom: 10,
                      }}
                    >
                      Business Detail
                    </Typography>
                    <Typography
                      style={{
                        fontSize: 28,
                        lineHeight: 1.1,
                        fontWeight: 700,
                        color: "#0F172A",
                        letterSpacing: "-0.03em",
                      }}
                    >
                      {business.name}
                    </Typography>
                    <Typography
                      style={{
                        marginTop: 10,
                        color: "#64748B",
                        fontSize: 14,
                        lineHeight: 1.6,
                      }}
                    >
                      İşletme bilgileri, kredi durumu ve bağlı kullanıcıları aynı görünüm altında yönetin.
                    </Typography>
                  </div>
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <Button
                      variant="outlined"
                      startIcon={<ArrowBackIcon />}
                      onClick={() => this.props.history.push("/businesses")}
                    >
                      Listeye Dön
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<EditIcon />}
                      onClick={() => this.setState({ businessDialogOpen: true })}
                    >
                      Düzenle
                    </Button>
                    <Button
                      variant="contained"
                      color="error"
                      startIcon={<BlockIcon />}
                      disabled={Boolean(business.deactive) || this.state.businessSaving}
                      onClick={this.handleDeleteBusiness}
                    >
                      Sil
                    </Button>
                  </div>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                    gap: 16,
                    padding: "22px 28px 8px 28px",
                  }}
                >
                  <SummaryCard
                    icon={<BusinessIcon sx={{ color: "#0F766E" }} />}
                    label="Durum"
                    value={business.deactive ? "Pasif" : "Aktif"}
                    tone="rgba(13, 148, 136, 0.10)"
                  />
                  <SummaryCard
                    icon={<GroupsIcon sx={{ color: "#2563EB" }} />}
                    label="Toplam Kullanıcı"
                    value={totalUserCount}
                    tone="rgba(37, 99, 235, 0.10)"
                  />
                  <SummaryCard
                    icon={<CheckCircleIcon sx={{ color: "#EA580C" }} />}
                    label="Aktif Kullanıcı"
                    value={activeUserCount}
                    tone="rgba(234, 88, 12, 0.10)"
                  />
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
                    gap: 16,
                    padding: "0 28px 28px 28px",
                  }}
                >
                  <DetailSection label="İşletme Bilgileri">
                    <DetailText first label="İşletme Adı" value={business.name} />
                    <DetailText label="E-posta" value={API.formatText(business.email)} />
                    <DetailText label="Telefon" value={API.formatText(business.phone)} />
                    <DetailText label="Yetkili Kişi" value={API.formatText(business.contactName)} />
                    <DetailText label="Adres" value={API.formatText(business.address)} />
                  </DetailSection>

                  <DetailSection label="Operasyon Bilgileri">
                    <DetailText
                      first
                      label="Şehir"
                      value={API.getCityName(business.city) || "—"}
                    />
                    <DetailText label="Credits" value={API.formatText(business.credits)} />
                    <DetailText
                      label="Free Credits"
                      value={API.formatText(business.freeCredits)}
                    />
                    <DetailText
                      label="Online Kiosk ID"
                      value={API.formatText(business.onlineKioskId)}
                    />
                    <DetailText
                      label="Kurumsal"
                      value={business.isCorporate ? "Evet" : "Hayır"}
                    />
                    <DetailText
                      label="Oluşturulma"
                      value={API.formatDateTR(business.createdAt)}
                    />
                    <DetailText
                      label="Güncellenme"
                      value={API.formatDateTR(business.updatedAt)}
                    />
                  </DetailSection>
                </div>
              </Badge>

              <Badge style={{ marginTop: 24, padding: 0 }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    padding: "24px 28px 18px 28px",
                    borderBottom: "1px solid #E7EDF6",
                    gap: 16,
                    flexWrap: "wrap",
                  }}
                >
                  <div>
                    <Typography variant="h6" fontWeight={700}>
                      İşletme Kullanıcıları
                    </Typography>
                    <Typography style={{ marginTop: 8, color: "#64748B" }}>
                      Kullanıcıları arayın, düzenleyin, şifre sıfırlayın veya silin.
                    </Typography>
                  </div>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() =>
                      this.setState({ userDialogOpen: true, selectedUser: undefined })
                    }
                  >
                    Kullanıcı Ekle
                  </Button>
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: 16,
                    flexWrap: "wrap",
                    padding: "20px 28px",
                    alignItems: "center",
                  }}
                >
                  <TextField
                    label="Kullanıcı ara"
                    value={this.state.userSearch}
                    onChange={(e) => this.setState({ userSearch: e.target.value })}
                    style={{ minWidth: 280, flex: 1 }}
                    InputProps={{
                      endAdornment: <SearchIcon sx={{ color: "#94A3B8" }} />,
                    }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={this.state.includeInactiveUsers}
                        onChange={(e) =>
                          this.setState({ includeInactiveUsers: e.target.checked })
                        }
                      />
                    }
                    label="Pasifleri göster"
                  />
                </div>

                <div style={{ padding: "0 16px 16px 16px" }}>
                  <DataGrid
                    autoHeight
                    getRowId={(row) => row._id}
                    rows={filteredUsers}
                    loading={this.state.usersLoading}
                    disableRowSelectionOnClick
                    initialState={{
                      pagination: { paginationModel: { pageSize: 10, page: 0 } },
                    }}
                    pageSizeOptions={[10, 25, 50]}
                    sx={{
                      border: "none",
                      "& .MuiDataGrid-cell": { borderColor: "#EEF2F7" },
                      "& .MuiDataGrid-columnHeaders": {
                        backgroundColor: "#F8FAFC",
                        borderBottom: "1px solid #E7EDF6",
                      },
                    }}
                    columns={[
                      {
                        field: "name",
                        headerName: "Kullanıcı",
                        flex: 1.1,
                        minWidth: 180,
                        renderCell: ({ row }) => (
                          <div style={{ display: "flex", flexDirection: "column" }}>
                            <Typography fontWeight={600}>{row.name}</Typography>
                            <Typography variant="body2" color="#64748B">
                              Rol: {API.formatText(row.role ?? 1)}
                            </Typography>
                          </div>
                        ),
                      },
                      {
                        field: "email",
                        headerName: "E-posta",
                        flex: 1.1,
                        minWidth: 190,
                      },
                      {
                        field: "phone",
                        headerName: "Telefon",
                        flex: 1,
                        minWidth: 160,
                      },
                      {
                        field: "deactive",
                        headerName: "Durum",
                        width: 120,
                        align: "center",
                        headerAlign: "center",
                        renderCell: ({ row }) => <StatusPill active={!row.deactive} />,
                      },
                      {
                        field: "createdAt",
                        headerName: "Oluşturulma",
                        width: 180,
                        valueGetter: (_, row) => API.formatDateTR(row.createdAt),
                      },
                      {
                        field: "actions",
                        headerName: "Aksiyon",
                        sortable: false,
                        width: 220,
                        renderCell: ({ row }) => (
                          <div style={{ display: "flex", gap: 8 }}>
                            <Button
                              size="small"
                              startIcon={<EditIcon />}
                              onClick={(e) => {
                                e.stopPropagation();
                                this.setState({
                                  userDialogOpen: true,
                                  selectedUser: row,
                                });
                              }}
                            >
                              Düzenle
                            </Button>
                            <Button
                              size="small"
                              startIcon={<ArrowForwardIcon />}
                              onClick={(e) => {
                                e.stopPropagation();
                                this.props.history.push(`/client/${row._id}`);
                              }}
                            >
                              Detay
                            </Button>
                          </div>
                        ),
                      },
                    ]}
                  />
                </div>
              </Badge>
            </div>
          </div>

          <BusinessFormDialog
            open={this.state.businessDialogOpen}
            business={this.state.business}
            saving={this.state.businessSaving}
            onClose={() => this.setState({ businessDialogOpen: false, businessSaving: false })}
            onSubmit={this.handleSaveBusiness}
          />

          <BusinessUserFormDialog
            open={this.state.userDialogOpen}
            businessId={business._id}
            user={this.state.selectedUser}
            saving={this.state.userSaving}
            onClose={() =>
              this.setState({
                userDialogOpen: false,
                selectedUser: undefined,
                userSaving: false,
              })
            }
            onDelete={this.handleDeleteUser}
            onResetPassword={this.handleResetPassword}
            onSubmit={this.handleSaveUser}
          />

          <PasswordResultDialog
            open={this.state.passwordDialogOpen}
            password={this.state.generatedPassword}
            onClose={() =>
              this.setState({
                passwordDialogOpen: false,
                generatedPassword: "",
              })
            }
          />
        </div>
      </div>
    );
  }
}

const DetailSection = ({ label, children }) => (
  <div
    style={{
      border: "1px solid #E7EDF6",
      borderRadius: 14,
      overflow: "hidden",
      backgroundColor: "#FCFDFE",
    }}
  >
    <div
      style={{
        padding: "14px 18px",
        borderBottom: "1px solid #E7EDF6",
        backgroundColor: "#F8FAFC",
      }}
    >
      <Typography style={{ fontSize: 13, fontWeight: 700, color: "#475569" }}>
        {label}
      </Typography>
    </div>
    <div style={{ padding: "0 18px" }}>{children}</div>
  </div>
);

const DetailText = ({ first, label, value }) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      gap: 16,
      padding: first ? "18px 0 14px 0" : "14px 0",
      borderTop: first ? "none" : "1px solid #E7EDF6",
      alignItems: "flex-start",
    }}
  >
    <Typography style={{ color: "#64748B", minWidth: 120 }}>{label}</Typography>
    <Typography
      style={{
        color: "#0F172A",
        fontWeight: 600,
        textAlign: "right",
        wordBreak: "break-word",
      }}
    >
      {value}
    </Typography>
  </div>
);

const StatusPill = ({ active }) => (
  <div
    style={{
      backgroundColor: active ? "rgba(22, 163, 74, 0.12)" : "rgba(220, 38, 38, 0.12)",
      color: active ? "#166534" : "#B91C1C",
      padding: "3px 10px",
      borderRadius: 999,
      fontWeight: 700,
      fontSize: 11,
      lineHeight: 1.2,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      minHeight: 24,
      whiteSpace: "nowrap",
    }}
  >
    {active ? "Aktif" : "Pasif"}
  </div>
);

const SummaryCard = ({ icon, label, value, tone }) => (
  <div
    style={{
      background: "#F8FAFC",
      borderRadius: 14,
      border: "1px solid #E7EDF6",
      padding: 18,
      display: "flex",
      alignItems: "center",
      gap: 14,
    }}
  >
    <div
      style={{
        width: 44,
        height: 44,
        borderRadius: 12,
        background: tone,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {icon}
    </div>
    <div>
      <Typography style={{ color: "#64748B", fontSize: 13, fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography style={{ color: "#0F172A", fontSize: 24, fontWeight: 700 }}>
        {value}
      </Typography>
    </div>
  </div>
);

const Badge = (props) => {
  return (
    <div
      style={{
        backgroundColor: "white",
        display: "flex",
        flexDirection: "column",
        flex: 1,
        border: "none",
        boxShadow: "0 12px 28px rgba(15, 23, 42, 0.06)",
        borderRadius: 16,
        padding: 24,
        color: "#0B1C30",
        position: "relative",
        ...props.style,
      }}
    >
      {props.children}
    </div>
  );
};
